import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  Checkbox, 
  Divider, 
  FormControl, 
  FormControlLabel, 
  FormGroup, 
  FormLabel, 
  Grid, 
  MenuItem, 
  Stack, 
  TextField, 
  Tab,
  Tabs,
  Typography 
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { RHFCheckbox, RHFSelect, RHFTextField } from '../../../../components/hook-form';
import FormProvider from '../../../../components/hook-form/FormProvider';
import { TabContext, TabPanel } from '@mui/lab';
import { useParams } from 'react-router-dom';
import { enqueueSnackbar } from '../../../../components/snackbar';
import { MecsCuesService } from '../../../../api/clinical-pathways/mecs-cues.service';
import { useAppContext } from '../../../../providers/AppContext';

// Define form values type
type FormValuesProps = {
  // Presenting Complaint
  symptomOnset: string;
  symptomNature: string;
  affectedEye: string[];
  symptoms: {
    pain: boolean;
    painSeverity: string;
    redness: boolean;
    photophobia: boolean;
    watering: boolean;
    discharge: boolean;
    dischargeType: string;
    visionChange: boolean;
    visionChangeType: string;
    flashesFloaters: boolean;
    headache: boolean;
    nausea: boolean;
  };
  
  // History of Presenting Complaint
  triggers: string;
  previousEpisodes: string;
  relievingFactors: string;
  aggravatingFactors: string;
  systemicSymptoms: string;
  
  // Ocular & Medical History
  previousEyeConditions: string;
  currentMedications: string;
  contactLensUse: boolean;
  contactLensDetails: string;
  allergies: string;
  medicalHistory: string;
  
  // Visual Acuity
  distanceVARight: string;
  distanceVALeft: string;
  distanceVABinocular: string;
  nearVARight: string;
  nearVALeft: string;
  pinholeVARight: string;
  pinholeVALeft: string;
  
  // External Examination
  lids: string;
  conjunctiva: string;
  cornea: string;
  anteriorChamber: string;
  iris: string;
  pupilReaction: string;
  rapd: string;
  
  // Intraocular Pressure
  iopRight: string;
  iopLeft: string;
  
  // Fundus Examination
  fundusFindings: string;
  
  // Additional Tests
  visualFields: string;
  cornealSensitivity: string;
  colorVision: string;
  
  // Clinical Impression
  diagnosis: string;
  managementPlan: string;
  treatmentPrescribed: string;
  referralRequired: boolean;
  referralDetails: string;
  followUpRequired: boolean;
  followUpTimeline: string;
  safetyNettingAdvice: string;
};

const SYMPTOM_NATURE_OPTIONS = [
  'Sudden',
  'Gradual',
  'Constant',
  'Intermittent',
  'Worsening',
  'Improving'
];

const EYE_OPTIONS = [
  { value: 'right', label: 'Right Eye' },
  { value: 'left', label: 'Left Eye' },
  { value: 'both', label: 'Both Eyes' },
];

const DISCHARGE_TYPES = [
  'Watery',
  'Mucous',
  'Purulent',
  'Mucopurulent',
  'None'
];

const VISION_CHANGE_TYPES = [
  'Blurred vision',
  'Reduced vision',
  'Distorted vision',
  'Floaters',
  'Flashes',
  'Halos',
  'None'
];

const FOLLOW_UP_TIMELINES = [
  '24 hours',
  '48 hours',
  '1 week',
  '2 weeks',
  '1 month',
  '3 months',
  '6 months',
  'As needed'
];

type Props = {
  clinicalRecordId?: number;
  mecsRecord?: any;
  onUpdate?: () => void;
};



function MecsCuesForm({ clinicalRecordId, mecsRecord, onUpdate }: Props) {
  const sections = [
    { id: 'presentingComplaint', label: 'Presenting Complaint' },
    { id: 'history', label: 'History' },
    { id: 'examination', label: 'Examination' },
    { id: 'visualAcuity', label: 'Visual Acuity' },
    { id: 'externalExam', label: 'External Exam' },
    { id: 'fundusExam', label: 'Fundus Exam' },
    { id: 'diagnosis', label: 'Diagnosis' },
  ] as const;
  
  type SectionId = typeof sections[number]['id'];
  
  const nextSection = () => {
    const currentIndex = sections.findIndex(s => s.id === currentSection);
    if (currentIndex < sections.length - 1) {
      setCurrentSection(sections[currentIndex + 1].id);
      window.scrollTo(0, 0);
    }
  };
  
  const prevSection = () => {
    const currentIndex = sections.findIndex(s => s.id === currentSection);
    if (currentIndex > 0) {
      setCurrentSection(sections[currentIndex - 1].id);
      window.scrollTo(0, 0);
    }
  };
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: SectionId) => {
    setCurrentSection(newValue);
  };
  
  const [currentSection, setCurrentSection] = useState<SectionId>(sections[0].id);
  
  const defaultValues = {
    symptomOnset: '',
    symptomNature: '',
    affectedEye: [],
    symptoms: {
      pain: false,
      painSeverity: '',
      redness: false,
      photophobia: false,
      watering: false,
      discharge: false,
      dischargeType: '',
      visionChange: false,
      visionChangeType: '',
      flashesFloaters: false,
      headache: false,
      nausea: false,
    },
    triggers: '',
    previousEpisodes: '',
    relievingFactors: '',
    aggravatingFactors: '',
    systemicSymptoms: '',
    previousEyeConditions: '',
    currentMedications: '',
    contactLensUse: false,
    contactLensDetails: '',
    allergies: '',
    medicalHistory: '',
    distanceVARight: '',
    distanceVALeft: '',
    distanceVABinocular: '',
    nearVARight: '',
    nearVALeft: '',
    pinholeVARight: '',
    pinholeVALeft: '',
    lids: '',
    conjunctiva: '',
    cornea: '',
    anteriorChamber: '',
    iris: '',
    pupilReaction: '',
    rapd: '',
    iopRight: '',
    iopLeft: '',
    fundusFindings: '',
    visualFields: '',
    cornealSensitivity: '',
    colorVision: '',
    diagnosis: '',
    managementPlan: '',
    treatmentPrescribed: '',
    referralRequired: false,
    referralDetails: '',
    followUpRequired: false,
    followUpTimeline: '',
    safetyNettingAdvice: '',
  };

  const methods = useForm<FormValuesProps>({
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    watch,
    reset,
  } = methods;

  const { patientId } = useParams();
  const { getAllClinicalRecords } = useAppContext();

  const watchSymptoms = watch('symptoms');

  const onSubmit = async (data: FormValuesProps) => {
    try {
      const service = new MecsCuesService();
      const payload = { ...data, clinicalRecordId };
      if (mecsRecord?.id) {
        await service.updateMecs(patientId!, mecsRecord.id, payload);
        enqueueSnackbar('MECS/CUES updated successfully', { variant: 'success' });
      } else {
        await service.createMecs(patientId!, payload);
        enqueueSnackbar('MECS/CUES saved successfully', { variant: 'success' });
      }
      getAllClinicalRecords(patientId);
      onUpdate && onUpdate();
    } catch (error) {
      enqueueSnackbar('Failed to save MECS/CUES', { variant: 'error' });
    }
  };

  useEffect(() => {
    if (mecsRecord && mecsRecord.id) {
      const flat = {
        ...(mecsRecord.presentingComplaint || {}),
        ...(mecsRecord.historyOfComplaint || {}),
        ...(mecsRecord.ocularMedicalHistory || {}),
        ...(mecsRecord.visualAcuity || {}),
        ...(mecsRecord.externalExam || {}),
        ...(mecsRecord.intraocularPressure || {}),
        ...(mecsRecord.fundusExamination || {}),
        ...(mecsRecord.additionalTests || {}),
        ...(mecsRecord.clinicalImpression || {}),
      } as Partial<FormValuesProps>;
      const normalized: FormValuesProps = {
        ...defaultValues,
        ...flat,
        affectedEye: Array.isArray(flat.affectedEye) ? (flat.affectedEye as string[]) : [],
        symptoms: typeof flat.symptoms === 'object' && flat.symptoms ? flat.symptoms as any : defaultValues.symptoms,
        referralRequired: !!flat.referralRequired,
        followUpRequired: !!flat.followUpRequired,
      };
      reset(normalized);
    }
  }, [mecsRecord, reset]);

  const [sectionDefaults] = useState<Record<SectionId, Partial<FormValuesProps>>>({
    presentingComplaint: {
      symptomOnset: '',
      symptomNature: '',
      affectedEye: [],
      symptoms: {
        pain: false,
        painSeverity: '',
        redness: false,
        photophobia: false,
        watering: false,
        discharge: false,
        dischargeType: '',
        visionChange: false,
        visionChangeType: '',
        flashesFloaters: false,
        headache: false,
        nausea: false,
      },
    },
    history: {
      triggers: '',
      previousEpisodes: '',
      relievingFactors: '',
      aggravatingFactors: '',
      systemicSymptoms: '',
    },
    examination: {
      previousEyeConditions: '',
      currentMedications: '',
      contactLensUse: false,
      contactLensDetails: '',
      allergies: '',
      medicalHistory: '',
    },
    visualAcuity: {
      distanceVARight: '',
      distanceVALeft: '',
      distanceVABinocular: '',
      nearVARight: '',
      nearVALeft: '',
      pinholeVARight: '',
      pinholeVALeft: '',
    },
    externalExam: {
      lids: '',
      conjunctiva: '',
      cornea: '',
      anteriorChamber: '',
      iris: '',
      pupilReaction: '',
      rapd: '',
      iopRight: '',
      iopLeft: '',
    },
    fundusExam: {
      fundusFindings: '',
      visualFields: '',
      cornealSensitivity: '',
      colorVision: '',
    },
    diagnosis: {
      diagnosis: '',
      managementPlan: '',
      treatmentPrescribed: '',
      referralRequired: false,
      referralDetails: '',
      followUpRequired: false,
      followUpTimeline: '',
      safetyNettingAdvice: '',
    },
  });
  
  const handleResetSection = () => {
    const sectionDefaultValues = sectionDefaults[currentSection];
    
    // Reset only the fields in the current section
    reset({
      ...methods.getValues(), // Keep current form values
      ...sectionDefaultValues, // Override with section defaults
    });
  
    enqueueSnackbar(`${sections.find(s => s.id === currentSection)?.label} reset successfully`, { 
      variant: 'success' 
    });
  };

  const renderPresentingComplaintSection = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Presenting Complaint
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="symptomOnset"
              label="When did symptoms start?"
              size="small"
              fullWidth
              placeholder="e.g., 2 days ago, this morning"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFSelect
              name="symptomNature"
              label="Nature of Symptoms"
              size="small"
              fullWidth
            >
              {SYMPTOM_NATURE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </RHFSelect>
          </Grid>
          <Grid item xs={12}>
            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <FormLabel component="legend">Affected Eye(s)</FormLabel>
              <FormGroup row>
                {EYE_OPTIONS.map((eye) => (
                  <RHFCheckbox
                    key={eye.value}
                    name={`affectedEye.${eye.value}`}
                    label={eye.label}
                  />
                ))}
              </FormGroup>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Symptoms (check all that apply):
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <RHFCheckbox name="symptoms.pain" label="Pain" />
                {watchSymptoms.pain && (
                  <RHFSelect
                    name="symptoms.painSeverity"
                    label="Pain Severity (1-10)"
                    size="small"
                    fullWidth
                    sx={{ mt: 1, ml: 4 }}
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                      <MenuItem key={num} value={num.toString()}>
                        {num} {num === 10 ? '(Worst)' : num === 1 ? '(Mildest)' : ''}
                      </MenuItem>
                    ))}
                  </RHFSelect>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <RHFCheckbox name="symptoms.redness" label="Redness" />
              </Grid>
              <Grid item xs={12} md={6}>
                <RHFCheckbox name="symptoms.photophobia" label="Light Sensitivity" />
              </Grid>
              <Grid item xs={12} md={6}>
                <RHFCheckbox name="symptoms.watering" label="Watering" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <RHFCheckbox name="symptoms.discharge" label="Discharge" />
                  {watchSymptoms.discharge && (
                    <RHFSelect
                      name="symptoms.dischargeType"
                      size="small"
                      sx={{ ml: 1, minWidth: 150 }}
                    >
                      {DISCHARGE_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </RHFSelect>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <RHFCheckbox name="symptoms.visionChange" label="Vision Change" />
                  {watchSymptoms.visionChange && (
                    <RHFSelect
                      name="symptoms.visionChangeType"
                      size="small"
                      sx={{ ml: 1, minWidth: 200 }}
                    >
                      {VISION_CHANGE_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </RHFSelect>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <RHFCheckbox name="symptoms.flashesFloaters" label="Flashes/Floaters" />
              </Grid>
              <Grid item xs={12} md={6}>
                <RHFCheckbox name="symptoms.headache" label="Headache" />
              </Grid>
              <Grid item xs={12} md={6}>
                <RHFCheckbox name="symptoms.nausea" label="Nausea" />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderHistoryPresentingComplaint = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          History of Presenting Complaint
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <RHFTextField
              name="triggers"
              label="Any known triggers?"
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <RHFTextField
              name="previousEpisodes"
              label="Previous similar episodes?"
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="relievingFactors"
              label="Relieving factors"
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="aggravatingFactors"
              label="Aggravating factors"
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <RHFTextField
              name="systemicSymptoms"
              label="Any other systemic symptoms?"
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderOcularMedicalHistory = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Ocular & Medical History
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <RHFTextField
              name="previousEyeConditions"
              label="Previous eye conditions"
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <RHFTextField
              name="currentMedications"
              label="Current medications"
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <RHFCheckbox name="contactLensUse" label="Contact lens wearer" />
              {watch('contactLensUse') && (
                <RHFTextField
                  name="contactLensDetails"
                  label="Details (type, wearing schedule, etc.)"
                  size="small"
                  fullWidth
                  sx={{ ml: 2 }}
                />
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="allergies"
              label="Allergies"
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="medicalHistory"
              label="Relevant medical history"
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderVisualAcuity = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Visual Acuity
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2">Distance VA</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <RHFTextField
              name="distanceVARight"
              label="Right Eye (R)"
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <RHFTextField
              name="distanceVALeft"
              label="Left Eye (L)"
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <RHFTextField
              name="distanceVABinocular"
              label="Binocular"
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Near VA (if applicable)
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="nearVARight"
              label="Right Eye (R)"
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="nearVALeft"
              label="Left Eye (L)"
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Pinhole VA (if reduced)
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="pinholeVARight"
              label="Right Eye (R)"
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="pinholeVALeft"
              label="Left Eye (L)"
              size="small"
              fullWidth
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderExternalExamination = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          External Examination
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="lids"
              label="Lids"
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="conjunctiva"
              label="Conjunctiva"
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="cornea"
              label="Cornea (stain with fluorescein if indicated)"
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="anteriorChamber"
              label="Anterior Chamber"
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="iris"
              label="Iris"
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="pupilReaction"
              label="Pupil Reaction"
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="rapd"
              label="RAPD (if applicable)"
              size="small"
              fullWidth
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderIOP = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Intraocular Pressure (IOP)
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="iopRight"
              label="Right Eye (mmHg)"
              size="small"
              type="number"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="iopLeft"
              label="Left Eye (mmHg)"
              size="small"
              type="number"
              fullWidth
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderFundusExamination = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Fundus Examination
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <RHFTextField
          name="fundusFindings"
          label="Findings"
          size="small"
          fullWidth
          multiline
          rows={4}
          placeholder="Optic disc, macula, vessels, periphery, etc."
        />
      </CardContent>
    </Card>
  );

  const renderAdditionalTests = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Additional Tests (if performed)
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="visualFields"
              label="Visual Fields"
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="cornealSensitivity"
              label="Corneal Sensitivity"
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="colorVision"
              label="Color Vision"
              size="small"
              fullWidth
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderClinicalImpression = () => {
    const watchReferralRequired = watch('referralRequired');
    const watchFollowUpRequired = watch('followUpRequired');

    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Clinical Impression & Management
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <RHFTextField
                name="diagnosis"
                label="Working Diagnosis"
                size="small"
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <RHFTextField
                name="managementPlan"
                label="Management Plan"
                size="small"
                fullWidth
                multiline
                rows={3}
                placeholder="e.g., Treatment given, advice provided, etc."
              />
            </Grid>
            <Grid item xs={12}>
              <RHFTextField
                name="treatmentPrescribed"
                label="Treatment Prescribed"
                size="small"
                fullWidth
                multiline
                rows={2}
                placeholder="Medications, dosage, frequency, duration"
              />
            </Grid>
            <Grid item xs={12}>
              <RHFCheckbox 
                name="referralRequired" 
                label="Referral required?" 
              />
              {watchReferralRequired && (
                <RHFTextField
                  name="referralDetails"
                  label="Referral Details"
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                  sx={{ mt: 1, ml: 4 }}
                  placeholder="Specialist, urgency, reason for referral"
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <RHFCheckbox 
                  name="followUpRequired" 
                  label="Follow-up required?" 
                />
                {watchFollowUpRequired && (
                  <RHFSelect
                    name="followUpTimeline"
                    label="Follow-up Timeline"
                    size="small"
                    sx={{ ml: 2, minWidth: 200 }}
                  >
                    {FOLLOW_UP_TIMELINES.map((timeline) => (
                      <MenuItem key={timeline} value={timeline}>
                        {timeline}
                      </MenuItem>
                    ))}
                  </RHFSelect>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <RHFTextField
                name="safetyNettingAdvice"
                label="Safety Netting Advice"
                size="small"
                fullWidth
                multiline
                rows={2}
                placeholder="Red flag symptoms to watch for, when to seek urgent care"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const renderSection = () => {
    switch (currentSection) {
      case 'presentingComplaint':
        return renderPresentingComplaintSection();
      case 'history':
        return renderHistoryPresentingComplaint();
      case 'examination':
        return renderOcularMedicalHistory();
      case 'visualAcuity':
        return renderVisualAcuity();
      case 'externalExam':
        return renderExternalExamination();
      case 'fundusExam':
        return renderFundusExamination();
      case 'diagnosis':
        return renderClinicalImpression();
      default:
        return null;
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Box sx={{ p: 2 }}>
        <Typography variant="h4" gutterBottom>
          MECS/CUES Form
        </Typography>
        <TabContext value={currentSection}>
          <Card>
            <Tabs
              value={currentSection}
              onChange={(e, newValue) => handleTabChange(e, newValue as SectionId)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                bgcolor: 'background.never',
                px: 2,
              }}
            >
              {sections.map((section) => (
                <Tab key={section.id} label={section.label} value={section.id} />
              ))}
            </Tabs>
            <CardContent>
              <TabPanel value="presentingComplaint">{renderPresentingComplaintSection()}</TabPanel>
              <TabPanel value="history">{renderHistoryPresentingComplaint()}</TabPanel>
              <TabPanel value="examination">{renderOcularMedicalHistory()}</TabPanel>
              <TabPanel value="visualAcuity">{renderVisualAcuity()}</TabPanel>
              <TabPanel value="externalExam">{renderExternalExamination()}</TabPanel>
              <TabPanel value="fundusExam">{renderFundusExamination()}</TabPanel>
              <TabPanel value="diagnosis">{renderClinicalImpression()}</TabPanel>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  variant="outlined"
                  onClick={prevSection}
                  disabled={sections[0].id === currentSection}
                >
                  Previous
                </Button>
                <Box sx={{ display: 'flex', gap: '10px'}}>
                <Button
                  variant="outlined"
                  onClick={handleResetSection}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  onClick={nextSection}
                  disabled={sections[sections.length - 1].id === currentSection}
                >
                  {/* {sections[sections.length - 1].id === currentSection ? 'Submit' : 'Next'} */}
                  Next
                </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </TabContext>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting}
            sx={{ ml: 2 }}
          >
            Save Changes
          </LoadingButton>
        </Box>
      </Box>
    </FormProvider>
  );
}

// Section rendering functions
const renderPresentingComplaintSection = () => (
  <Grid container spacing={2}>
    {/* Existing presenting complaint fields */}
  </Grid>
);

const renderHistorySection = () => (
  <Grid container spacing={2}>
    {/* History fields */}
  </Grid>
);

const renderExaminationSection = () => (
  <Grid container spacing={2}>
    {/* Examination fields */}
  </Grid>
);

const renderVisualAcuitySection = () => (
  <Grid container spacing={2}>
    {/* Visual Acuity fields */}
  </Grid>
);

const renderExternalExamSection = () => (
  <Grid container spacing={2}>
    {/* External Exam fields */}
  </Grid>
);

const renderFundusExamSection = () => (
  <Grid container spacing={2}>
    {/* Fundus Exam fields */}
  </Grid>
);

const renderDiagnosisSection = () => (
  <Grid container spacing={2}>
    {/* Diagnosis and management fields */}
  </Grid>
);

export default MecsCuesForm;
